import { employeeImportWorkflow, type BulkReferenceMatch, type BulkValidatedRow, type BulkWorkflow } from '../../bulk/public';
import type { HireReference, HireReferences, PeopleWorker } from './model';

type EmployeeImportReferences = HireReferences & { jobTypes: HireReference[] };

function referenceCatalogs(references: EmployeeImportReferences) {
  return {
    companyId: { label: '회사', values: references.companies },
    organizationId: { label: '소속 조직', values: references.organizations },
    locationId: { label: '근무지', values: references.locations },
    jobProfileId: { label: '직무', values: references.jobs },
    jobTypeId: { label: '고용 유형', values: references.jobTypes },
  };
}

export function createEmployeeImportWorkflow(references: EmployeeImportReferences, asOf: string): BulkWorkflow {
  const catalogs = referenceCatalogs(references);
  const referenceColumns = (keys: Array<keyof typeof catalogs>): BulkWorkflow['previewColumns'] => keys.map((key) => ({
    label: catalogs[key].label,
    referenceKey: key,
    value: (row: Record<string, string>) => row[key],
  }));

  return {
    ...employeeImportWorkflow,
    columns: employeeImportWorkflow.columns.map((column) => {
      const catalog = catalogs[column.key as keyof typeof catalogs];
      if (catalog) {
        const names = catalog.values.map((entry) => entry.descriptor?.trim()).filter((name): name is string => Boolean(name));
        const counts = new Map<string, number>();
        for (const name of names) counts.set(name, (counts.get(name) ?? 0) + 1);
        return { ...column, example: names.find((name) => counts.get(name) === 1) ?? `${catalog.label} 이름` };
      }
      if (column.key === 'legalFamilyName' || column.key === 'legalGivenName') return { ...column, example: '' };
      if (column.key === 'hireDate' || column.key === 'effectiveDate') return { ...column, example: asOf };
      return column;
    }),
    previewColumns: employeeImportWorkflow.previewColumns.flatMap((column) => {
      if (column.label === '회사·조직') return referenceColumns(['companyId', 'organizationId']);
      if (column.label === '직무·근무지') return referenceColumns(['jobProfileId', 'locationId', 'jobTypeId']);
      return [column];
    }),
  };
}

export function prepareEmployeeImportRows(rows: Record<string, string>[], references: EmployeeImportReferences, workers: readonly PeopleWorker[], asOf: string): BulkValidatedRow[] {
  const catalogs = referenceCatalogs(references);
  const prepared = rows.map((source, index) => {
    const values: Record<string, string> = { ...source, effectiveDate: source.effectiveDate || asOf };
    const errors: string[] = [];
    const referenceMatches: Record<string, BulkReferenceMatch> = {};
    for (const [key, catalog] of Object.entries(catalogs)) {
      const input = values[key]?.trim();
      if (!input) {
        referenceMatches[key] = { input: source[key] ?? '', status: 'MISSING' };
        continue;
      }
      const exact = catalog.values.find((entry) => entry.id === input);
      const matches = exact ? [exact] : catalog.values.filter((entry) => entry.descriptor?.trim().toLocaleLowerCase() === input.toLocaleLowerCase());
      if (matches.length !== 1) {
        referenceMatches[key] = { input: source[key], status: matches.length > 1 ? 'AMBIGUOUS' : 'NOT_FOUND', candidateCount: matches.length };
        errors.push(matches.length > 1 ? `${catalog.label} 이름이 중복됩니다. 검색 결과에서 선택해 주세요.` : `${catalog.label} '${input}'을 기준일에 사용할 수 없습니다. 기준정보를 확인해 주세요.`);
      } else {
        const matched = matches[0];
        referenceMatches[key] = { input: source[key], status: 'MATCHED', matched: { id: matched.id, descriptor: matched.descriptor?.trim() || matched.id } };
        Object.assign(values, { [key]: matched.id });
      }
    }
    if (values.effectiveDate !== asOf) errors.push(`이관 기준일은 선택한 기준일 ${asOf}로 통일해 주세요.`);
    if (source.hireDate && source.hireDate > values.effectiveDate) errors.push('입사일은 이관 기준일과 같거나 이전이어야 합니다.');
    if (source.workerNumber && workers.some((worker) => worker.companyId === values.companyId && worker.workerNumber.trim() === source.workerNumber.trim())) errors.push('이미 등록된 회사·사번입니다. 기존 구성원 상세에서 확인해 주세요.');
    return { rowNumber: index + 2, values, errors, referenceMatches };
  });
  const groups = new Map<string, BulkValidatedRow[]>();
  for (const row of prepared) {
    const values = row.values as Record<string,string>;
    const keys = [values.workerNumber && `number:${values.companyId}:${values.workerNumber}`, values.systemId && `system:${values.systemId}`].filter(Boolean);
    for (const key of keys) groups.set(key, [...(groups.get(key) ?? []), row]);
  }
  for (const entries of groups.values()) if (entries.length > 1) for (const row of entries) row.errors.push('붙여넣은 명단에 같은 회사·사번 또는 계정 ID가 중복됩니다.');
  return prepared;
}
